import axios, { AxiosRequestConfig } from 'axios';
import { useGuestStore, useAuthStore, useSessionStore, useNotificationStore } from '@host/stores';
import { ENTITLEMENT_UPGRADE_MESSAGE } from '@/shared/stores/entitlements.store';
import { useWelcomeStore } from '@/host/welcome/store/welcome.store';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import { Logger } from './logger.service';
import { updateClockOffsetFromDateHeader } from '../utils/clock-offset.util';

interface ApiRequestParams<T = any> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  service?: 'app' | 'legacy' | 'event';
  endpoint: string;
  data?: T;
  config?: AxiosRequestConfig;
  userType?: UserType;
  abortId?: string;
  group?: string;
}

export const enum UserType {
  PlatformUser,
  GuestUser,
}

const API_ROOTS = {
  app: import.meta.env.VITE_APP_API_ROOT || 'http://localhost:5500',
  legacy: import.meta.env.VITE_LEGACY_API_ROOT || 'http://localhost:7050',
  event: import.meta.env.VITE_EVENT_API_ROOT || 'http://localhost:5550',
};

const getApiRoot = (service: 'app' | 'legacy' | 'event'): string => {
  return API_ROOTS[service] || API_ROOTS.app;
};

const normalizeEndpoint = (endpoint: string): string => {
  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

export class ApiService {
  private static instance: ApiService;
  private static controllers = new Map<string, { controller: AbortController; group?: string }>();
  /** Set during an org switch so stray 401s don't hard-redirect over its own navigation. */
  static suppressAuthRedirect = false;

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /** The org pid sent as `x-org-pid`, for callers that must use fetch directly (e.g. SSE). */
  static getOrgPid(): string | null {
    return ApiService._getOrgPid();
  }

  private static _getOrgPid(): string | null {
    const guestStore = useGuestStore.getState();
    const organisationPid =
      useWelcomeStore.getState().welcomeSettings?.pid ??
      guestStore.visitDetails?.organization_pid ??
      guestStore.lobbyDetails?.pid ??
      localStorage.getItem('orgPid') ??
      null;
    return useSessionStore.getState().orgPid ?? organisationPid;
  }

  private static async _request<T>(params: ApiRequestParams, isRetry = false): Promise<T> {
    const {
      method = 'GET',
      service = 'app',
      endpoint,
      data,
      config,
      userType,
      abortId,
      group,
    } = params;

    let controller: AbortController | undefined;

    if (abortId && !isRetry) {
      // Abort previous request with same ID if exists
      if (this.controllers.has(abortId)) {
        console.warn(`🛑 Aborting previous request with id: ${abortId}`);
        this.controllers.get(abortId)?.controller.abort();
        this.controllers.delete(abortId);
      }

      controller = new AbortController();
      this.controllers.set(abortId, { controller, group });
    }

    try {
      // Auto-detect user type if not explicitly provided
      const effectiveUserType =
        userType ?? (isCurrentUserGuest() ? UserType.GuestUser : UserType.PlatformUser);

      const token =
        effectiveUserType === UserType.GuestUser
          ? null // For guests, rely on httpOnly cookies automatically sent by the browser
          : await useAuthStore.getState().getToken();

      const orgPid = ApiService._getOrgPid();

      const apiRoot = getApiRoot(service);

      const headers: Record<string, any> = {
        'x-org-pid': orgPid,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(orgPid && { 'x-org-pid': orgPid }),
        ...(config?.headers ?? {}),
      };

      // Only set Content-Type to application/json if data is not FormData
      // For FormData, let the browser set the Content-Type with boundary
      if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      // Exclude `headers` from config spread — they are already merged into `headers` above.
      // Spreading config directly would overwrite the built headers (including Authorization).
      const { headers: _configHeaders, signal: _configSignal, ...restConfig } = config ?? {};

      const response = await axios({
        url: `${apiRoot}${normalizeEndpoint(endpoint)}`,
        method,
        headers,
        ...(method === 'GET' ? { params: data } : { data }),
        ...restConfig,
        signal: controller?.signal || config?.signal,
        withCredentials: true,
      });

      updateClockOffsetFromDateHeader(response.headers?.date);
      if (service === 'app') {
        // 204 No Content — nothing to parse (e.g. DELETE success)
        if (response.status === 204) {
          return undefined as unknown as T;
        }

        // Assuming NestJS responses follow { success, message, data }
        const { success, message, data } = response.data;
        if (!success) {
          console.error(`❌ API Error (${endpoint}):`, message || 'API request failed.');
          throw new Error(message || 'API request failed.');
        }
        return data as T;
      }

      return response.data as T;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        // Do not log for intentional cancellations
      } else {
        console.error(
          `🚨 API Failure (${endpoint}):`,
          error?.response?.data || error?.message || error
        );
      }

      // Handle 401 Unauthorized
      const is401 = error.response?.status === 401;
      const effectiveUserType =
        userType ?? (isCurrentUserGuest() ? UserType.GuestUser : UserType.PlatformUser);
      const isGuest = effectiveUserType === UserType.GuestUser;
      const isPlatformUser = !isGuest;

      // Skip refresh endpoint to avoid infinite loops
      const isRefreshEndpoint = endpoint.includes('welcome/guest/refresh');

      if (is401 && isGuest && !isRetry && !isRefreshEndpoint) {
        Logger.debug('🔄 401 detected for guest, attempting token refresh...');

        try {
          // If another request is already refreshing, wait for it
          if (isRefreshing && refreshPromise) {
            Logger.debug('⏳ Waiting for ongoing refresh...');
            await refreshPromise;
          } else {
            // Start refresh process
            isRefreshing = true;
            refreshPromise = useGuestStore.getState().refreshGuestToken();
            await refreshPromise;
            isRefreshing = false;
            refreshPromise = null;
          }

          Logger.debug('✅ Token refreshed, retrying original request...');

          // Retry the original request with the new token
          return await ApiService._request<T>(params, true);
        } catch (refreshError: any) {
          console.error('❌ Token refresh failed:', refreshError);
          isRefreshing = false;
          refreshPromise = null;

          // If refresh token fails or is expired, trigger logout and redirect
          const errorMessage = refreshError?.message || refreshError?.response?.data?.message || '';
          if (
            errorMessage.includes('expired') ||
            errorMessage.includes('Invalid') ||
            errorMessage.includes('token') ||
            errorMessage.includes('refresh')
          ) {
            console.error('🚪 Guest refresh token expired or invalid, logging out...');
            useGuestStore.getState().resetGuest();

            // Navigate to login page
            const orgSlug = localStorage.getItem('org-slug');
            const welcomeRedirect = orgSlug ? `/${orgSlug}/login` : '/welcome';
            if (!ApiService.suppressAuthRedirect) window.location.href = welcomeRedirect;
          }

          // Throw the original error
          throw error?.response?.data || new Error('Authentication failed.');
        }
      }

      if (is401 && isPlatformUser && !isRetry) {
        Logger.debug('🔄 401 detected for platform user, attempting token refresh...');

        try {
          // If another request is already refreshing, wait for it
          if (isRefreshing && refreshPromise) {
            Logger.debug('⏳ Waiting for ongoing refresh...');
            await refreshPromise;
          } else {
            // Start refresh process
            isRefreshing = true;
            refreshPromise = useAuthStore
              .getState()
              .refreshToken()
              .then(() => undefined);
            await refreshPromise;
            isRefreshing = false;
            refreshPromise = null;
          }

          Logger.debug('✅ Platform token refreshed, retrying original request...');

          // Retry the original request with the new token
          return await ApiService._request<T>(params, true);
        } catch (refreshError: any) {
          console.error('❌ Platform user token refresh failed:', refreshError);
          isRefreshing = false;
          refreshPromise = null;

          // Session truly expired — sign out and redirect to login
          console.error('🚪 Platform user session expired, redirecting to login...');
          const orgSlug = localStorage.getItem('org-slug');
          try {
            await useAuthStore.getState().signOut();
          } catch {}
          const loginRedirect = orgSlug ? `/${orgSlug}/login` : '/welcome';
          if (!ApiService.suppressAuthRedirect) window.location.href = loginRedirect;

          throw error?.response?.data || new Error('Session expired. Please log in again.');
        }
      }

      // Plan-denied 403s (backend entitlementForbidden: body carries `feature`) get one
      // consistent upgrade prompt here, so every server-side gate surfaces the same way.
      // Still rethrown below — callers keep their own handling.
      const body = error?.response?.data;
      if (error?.response?.status === 403 && body?.feature) {
        useNotificationStore
          .getState()
          .showSnackbar(
            body.limit !== undefined && body.message ? body.message : ENTITLEMENT_UPGRADE_MESSAGE,
            'info'
          );
      }

      throw body || error;
    } finally {
      if (abortId && this.controllers.get(abortId)?.controller === controller) {
        this.controllers.delete(abortId);
      }
    }
  }

  private static async _getDirectAccessUrl(
    endpoint: string,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint'>
  ): Promise<string> {
    const { service = 'app', data, userType } = options || {};

    // Auto-detect user type if not explicitly provided
    const effectiveUserType =
      userType ?? (isCurrentUserGuest() ? UserType.GuestUser : UserType.PlatformUser);

    const token =
      effectiveUserType === UserType.GuestUser
        ? useGuestStore.getState().token || localStorage.getItem('guestToken')
        : await useAuthStore.getState().getToken();

    const orgPid = ApiService._getOrgPid();

    const apiRoot = getApiRoot(service);
    const url = new URL(`${apiRoot}${normalizeEndpoint(endpoint)}`);

    // Append query parameters
    if (data) {
      Object.entries(data).forEach(([key, value]) => url.searchParams.append(key, String(value)));
    }

    // Append headers as query parameters if needed (for GET redirects)
    if (token) url.searchParams.append('access_token', token);
    if (orgPid) url.searchParams.append('x-org-pid', orgPid);

    return url.toString();
  }

  // ========================================
  // STATIC METHODS (Backward Compatibility)
  // ========================================

  static request = <T>(params: ApiRequestParams): Promise<T> => {
    return ApiService._request<T>(params);
  };

  private static _isOptions(obj: any): boolean {
    return (
      obj &&
      typeof obj === 'object' &&
      ('abortId' in obj || 'group' in obj || 'service' in obj || 'config' in obj)
    );
  }

  static getDirectAccessUrl = async (
    endpoint: string,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint'>
  ): Promise<string> => {
    return ApiService._getDirectAccessUrl(endpoint, options);
  };

  static get = <T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ) => {
    // If only 2 arguments are provided and the 2nd looks like options, treat it as options
    if (data && !options && ApiService._isOptions(data)) {
      return ApiService._request<T>({ ...data, method: 'GET', endpoint });
    }
    return ApiService._request<T>({ ...options, method: 'GET', endpoint, data });
  };

  static post = <T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ) => ApiService._request<T>({ ...options, method: 'POST', endpoint, data });

  static put = <T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ) => ApiService._request<T>({ ...options, method: 'PUT', endpoint, data });

  static patch = <T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ) => ApiService._request<T>({ ...options, method: 'PATCH', endpoint, data });

  static delete = <T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ) => ApiService._request<T>({ ...options, method: 'DELETE', endpoint, data });

  static abort = (id: string) => {
    const entry = ApiService.controllers.get(id);
    if (entry) {
      entry.controller.abort();
      ApiService.controllers.delete(id);
    }
  };

  static abortGroup = (group: string) => {
    for (const [id, entry] of ApiService.controllers.entries()) {
      if (entry.group === group) {
        entry.controller.abort();
        ApiService.controllers.delete(id);
      }
    }
  };

  static abortAll = () => {
    for (const entry of ApiService.controllers.values()) {
      entry.controller.abort();
    }
    ApiService.controllers.clear();
  };

  static isCancel = (value: any): boolean => {
    return axios.isCancel(value);
  };

  // ========================================
  // INSTANCE METHODS (New Singleton Usage)
  // ========================================

  request<T>(params: ApiRequestParams): Promise<T> {
    return ApiService._request<T>(params);
  }

  getDirectAccessUrl(
    endpoint: string,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint'>
  ): Promise<string> {
    return ApiService._getDirectAccessUrl(endpoint, options);
  }

  get<T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ): Promise<T> {
    if (data && !options && ApiService._isOptions(data)) {
      return ApiService._request<T>({ ...data, method: 'GET', endpoint });
    }
    return ApiService._request<T>({ ...options, method: 'GET', endpoint, data });
  }

  post<T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ): Promise<T> {
    return ApiService._request<T>({ ...options, method: 'POST', endpoint, data });
  }

  put<T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ): Promise<T> {
    return ApiService._request<T>({ ...options, method: 'PUT', endpoint, data });
  }

  patch<T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ): Promise<T> {
    return ApiService._request<T>({ ...options, method: 'PATCH', endpoint, data });
  }

  delete<T = any>(
    endpoint: string,
    data?: any,
    options?: Omit<ApiRequestParams, 'method' | 'endpoint' | 'data'>
  ): Promise<T> {
    return ApiService._request<T>({ ...options, method: 'DELETE', endpoint, data });
  }

  abort(id: string) {
    ApiService.abort(id);
  }

  abortGroup(group: string) {
    ApiService.abortGroup(group);
  }

  abortAll() {
    ApiService.abortAll();
  }

  static postBeacon(endpoint: string, data: Record<string, unknown> = {}, method: string = 'POST') {
    const apiRoot = getApiRoot('app');
    const orgPid = ApiService._getOrgPid();

    // Get token for authentication
    // For guests: rely on httpOnly cookies (no Authorization header needed)
    // For platform users: need to get token from localStorage or auth store
    const isGuest = isCurrentUserGuest();
    const token = isGuest
      ? null // Guests use httpOnly cookies sent automatically
      : localStorage.getItem('accessToken'); // Get platform user token from localStorage

    const url = `${apiRoot}${normalizeEndpoint(endpoint)}`;

    Logger.debug('[ApiService] postBeacon called:', {
      url,
      method,
      isGuest,
      hasToken: !!token,
      hasOrgPid: !!orgPid,
      data,
    });

    return fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        ...(orgPid && { 'x-org-pid': orgPid }),
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      credentials: 'include', // Important: sends httpOnly cookies for guest auth
      body: JSON.stringify(data),
      keepalive: true, // Ensures request completes during page unload
    })
      .then((response) => {
        Logger.debug('[ApiService] postBeacon response:', response.status, response.statusText);
        if (!response.ok) {
          console.error(`ApiService Beacon failed with status: ${response.status}`);
        }
        return response;
      })
      .catch((err) => {
        console.error('ApiService Beacon network failure:', err);
        return Promise.reject(err);
      });
  }
}
